import React, { useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import { AlertCircle, ChevronDown, ChevronRight, Users, Lock, ShieldCheck, Send, Loader2 } from 'lucide-react';
import { api } from '../services/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import { usePermissionsEtatSalaires } from '../hooks/usePermissionsEtatSalaires';
import LoadingSpinner from './LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';

import EtatSalairesHeader from './etatSalaires/EtatSalairesHeader';
import EtatSalairesStatsCards from './etatSalaires/EtatSalairesStatsCards';
import CalendrierPaieSection from './etatSalaires/CalendrierPaieSection';
import SalaryTableSection from './etatSalaires/SalaryTableSection';
import OuvertureFenetreDialog from './etatSalaires/OuvertureFenetreDialog';
import { formatMontant, formatMonthLabel, normalizeFilePart } from './etatSalaires/etatSalairesUtils';

// Gabarits KLASYS des deux fenêtres (DESIGN-SYSTEM.md)
const CONTENU_DIALOG = 'w-[95vw] max-w-2xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0';
const ENTETE_DIALOG = 'flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left';
const PIED_DIALOG = 'gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0';
const BOUTON_PIED = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md px-4 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_SECONDAIRE = `${BOUTON_PIED} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
const BOUTON_PRINCIPAL = `${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a]`;

const CarteRecap = ({ libelle, valeur, accent = false }) => (
  <div className={`rounded-md border p-3 ${accent ? 'border-[#246990]/20 bg-[#eef7fb]' : 'border-gray-200 bg-white'}`}>
    <p className="text-xs text-gray-500">{libelle}</p>
    <p className={`mt-1 text-sm font-semibold ${accent ? 'text-[#246990]' : 'text-gray-900'}`}>{valeur}</p>
  </div>
);

const formatMissingPointageDate = (value) => {
  if (!value) return '';
  const date = new Date(String(value) + 'T12:00:00');
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
};

const RetenuesNonImputeesPanel = ({ resume = {} }) => {
  const lignes = Array.isArray(resume.lignes) ? resume.lignes : [];
  const total = Number(resume.total || 0);

  if (total <= 0 || !lignes.length) return null;

  const formatDetail = (details = []) => {
    const items = details
      .filter((item) => Number(item.non_imputee || 0) > 0)
      .map((item) => item.label + ' : ' + formatMontant(item.non_imputee));

    return items.length ? items.join(' ; ') : 'Dépassement global des déductions';
  };

  return (
    <div className="px-6">
      <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h5 className="font-semibold text-amber-950">Déductions non imputées</h5>
                <p className="mt-1 text-sm leading-6 text-amber-900">
                  Une déduction non imputée est une retenue ou une avance qui dépasse le brut disponible d'une ligne de paie.
                  Le net de la ligne reste plafonné à 0 FCFA ; ce montant est donc signalé mais ne réduit pas le net à payer du mois.
                </p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-sm font-bold text-amber-800 shadow-sm">
                {formatMontant(total)}
              </span>
            </div>

            <div className="mt-4 overflow-x-auto rounded-lg border border-amber-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-amber-100 text-xs uppercase text-amber-950">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Ligne concernée</th>
                    <th className="px-3 py-2 text-right font-semibold">Brut</th>
                    <th className="px-3 py-2 text-right font-semibold">Déductions calculées</th>
                    <th className="px-3 py-2 text-right font-semibold">Imputées</th>
                    <th className="px-3 py-2 text-right font-semibold">Non imputées</th>
                    <th className="px-3 py-2 text-left font-semibold">Origine</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100">
                  {lignes.map((ligne) => (
                    <tr key={ligne.ligne_key || [ligne.employe_id, ligne.categorie].join('-')} className="align-top">
                      <td className="px-3 py-2">
                        <p className="font-semibold text-gray-900">{ligne.nom_complet}</p>
                        <p className="text-xs text-gray-500">{ligne.categorie}</p>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-800">{formatMontant(ligne.salaire_brut)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-red-600">{formatMontant(ligne.retenues_total)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-green-700">{formatMontant(ligne.retenues_imputees)}</td>
                      <td className="px-3 py-2 text-right font-bold text-amber-700">{formatMontant(ligne.retenues_non_imputees)}</td>
                      <td className="max-w-[260px] px-3 py-2 text-xs leading-5 text-amber-900">
                        {formatDetail(ligne.retenues_non_imputees_detail)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const EtatSalaires = ({
  selectedDate,
  setSelectedDate,
  anneeActive,
  showToast,
  formatDateForDisplay,
  goToPreviousMonth,
  goToNextMonth,
  onBackToDashboard
}) => {
  const [loading, setLoading] = useState(true);
  const [etat, setEtat] = useState(null);
  const [showDotationConfirm, setShowDotationConfirm] = useState(false);
  const [sendingDotation, setSendingDotation] = useState(false);
  const [dotationSent, setDotationSent] = useState(false);
  const [showValidationConfirm, setShowValidationConfirm] = useState(false);
  const [validatingEtat, setValidatingEtat] = useState(false);

  // Sections dépliables, comme MaCaisse
  const [expandedSections, setExpandedSections] = useState(new Set(['calendrier', 'lignes']));
  // Ouverture exceptionnelle d'une étape : { mode: 'demander' | 'refermer', etape }
  const [ouvertureEnCours, setOuvertureEnCours] = useState(null);
  const [cleOuverture, setCleOuverture] = useState(0);
  const ouvrirFenetreOuverture = (mode, etape) => {
    setCleOuverture((cle) => cle + 1);
    setOuvertureEnCours({ mode, etape });
  };

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionName)) newSet.delete(sectionName); else newSet.add(sectionName);
      return newSet;
    });
  };

  const selectedYearId = anneeActive?.id || localStorage.getItem('annee_scolaire_id');

  const params = useMemo(() => ({
    annee_scolaire_id: selectedYearId,
    mois: selectedDate.getMonth() + 1,
    annee: selectedDate.getFullYear()
  }), [selectedDate, selectedYearId]);

  const loadEtat = async () => {
    setLoading(true);
    try {
      const response = await api.etatSalaires.getEtat(params);
      if (!response.success) {
        throw new Error(response.message || response.error || 'État des salaires indisponible');
      }
      setEtat(response.data);
    } catch (error) {
      showToast(error.message || "Erreur lors du chargement de l'état des salaires", 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedYearId) loadEtat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYearId, selectedDate]);

  useEffect(() => {
    setDotationSent(false);
  }, [params.annee_scolaire_id, params.mois, params.annee]);

  const resume = etat?.resume || {};
  const calendrier = etat?.calendrier || {};
  const monthLabel = formatMonthLabel(etat?.mois_scolaire);
  const retenuesNonImputeesResume = etat?.retenues_non_imputees_resume || {};
  const montantDotation = Math.round(Number(resume.reste_a_doter || 0));
  const autorisationPaiement = etat?.autorisation_paiement || null;
  const dotationReasons = autorisationPaiement?.raisons_dotation || [];
  const canRequestSalaryDotation = !autorisationPaiement || autorisationPaiement.peut_demander_dotation !== false;
  const dotationLockMessage = dotationReasons[0] || "La demande de dotation salaires n'est pas autorisée pour ce mois.";
  const dotationLockTitle = autorisationPaiement?.demande_dotation_acceptee
    ? 'Demande déjà validée'
    : autorisationPaiement?.demande_dotation_en_attente
      ? 'Demande en cours de traitement'
      : 'Demande de dotation verrouillée';
  const validationReasons = autorisationPaiement?.raisons_validation || [];
  // Valider est réservé aux profils Comptable et Directeur Général : le bouton
  // ne s'offre qu'à eux, le serveur refuse les autres.
  const { peutValider: peutValiderEtatSalaires, peutOuvrirCalendrier } = usePermissionsEtatSalaires();
  const canValidateEtat = autorisationPaiement?.peut_valider_etat === true && peutValiderEtatSalaires;
  const etatValidated = autorisationPaiement?.validation_couvre_etat === true;
  const validationLockMessage = validationReasons[0] || "L'état des salaires ne peut pas encore être validé pour ce mois.";
  const validationInfoTitle = etatValidated
    ? 'État des salaires validé'
    : canValidateEtat
      ? 'Validation comptable disponible'
      : "Validation de l'état verrouillée";
  const showValidationNotice = etatValidated || canValidateEtat || validationReasons.length > 0;
  const showSalaryStateNotices = Boolean(!canRequestSalaryDotation || showValidationNotice);
  const hasSalaryBlockingNotice = !canRequestSalaryDotation || (!etatValidated && !canValidateEtat && validationReasons.length > 0);
  const salaryNoticeTone = hasSalaryBlockingNotice
    ? { panel: 'border-amber-200 bg-amber-50', title: 'text-amber-950', text: 'text-amber-900', icon: 'text-amber-600', list: 'border-amber-200 divide-amber-200' }
    : etatValidated
      ? { panel: 'border-green-200 bg-green-50', title: 'text-green-950', text: 'text-green-900', icon: 'text-green-600', list: 'border-green-200 divide-green-200' }
      : { panel: 'border-blue-200 bg-blue-50', title: 'text-blue-950', text: 'text-blue-900', icon: 'text-blue-600', list: 'border-blue-200 divide-blue-200' };
  const validationStatusMessage = etatValidated
    ? "La comptabilité a validé cet état pour le mois lu. Les modules de paiement peuvent être ouverts si la dotation couvre le reste à payer."
    : canValidateEtat
      ? "La dotation centrale est disponible et les contrôles sont bons. La comptabilité peut valider l'état pour débloquer les paiements."
      : validationLockMessage;

  const buildDotationJustificatifFile = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const maxWidth = pageWidth - (margin * 2);
    let y = 16;

    const ensureSpace = (height = 10) => {
      if (y + height > pageHeight - 16) {
        doc.addPage();
        y = 16;
      }
    };

    const writeLine = (text, options = {}) => {
      const { size = 9, bold = false, color = [15, 23, 42], gap = 5, indent = 0 } = options;
      ensureSpace(gap + 3);
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(text || ''), maxWidth - indent);
      doc.text(lines, margin + indent, y);
      y += Math.max(gap, lines.length * (size * 0.38));
    };

    const drawDivider = () => {
      ensureSpace(6);
      doc.setDrawColor(210, 220, 230);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
    };

    doc.setFillColor(36, 105, 144);
    doc.rect(0, 0, pageWidth, 26, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Etat des salaires - demande de dotation', margin, 13);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Mois lu : ${monthLabel}`, margin, 20);
    y = 34;

    writeLine('Resume de la demande', { size: 12, bold: true, color: [36, 105, 144], gap: 7 });
    writeLine(`Net a doter : ${formatMontant(montantDotation)}`, { size: 11, bold: true });
    writeLine(`${resume.effectif_unique || resume.effectif || 0} employe(s) concerne(s) pour ${resume.effectif || 0} ligne(s) de paie`);
    writeLine(`Salaire brut total : ${formatMontant(resume.salaire_brut_total)}`);
    writeLine(`Retenues totales : ${formatMontant(resume.retenues_total)}`);
    writeLine(`Avances deductibles : ${formatMontant(resume.avances_total)}`);
    writeLine(`Deja paye : ${formatMontant(resume.deja_paye_total)}`);
    drawDivider();

    writeLine('Calendrier mensuel de paie', { size: 12, bold: true, color: [36, 105, 144], gap: 7 });
    writeLine(`Avances : ${calendrier.avances || '-'}`);
    writeLine(`Etat des salaires : ${calendrier.etat_salaires || '-'}`);
    writeLine(`Demande de dotation : ${calendrier.demande_dotation || '-'}`);
    writeLine(`Validation centrale : ${calendrier.validation_centrale || '-'}`);
    writeLine(`Paiement : ${calendrier.paiement_salaires || '-'}`);
    drawDivider();

    writeLine('Detail par personne', { size: 12, bold: true, color: [36, 105, 144], gap: 7 });

    const sections = [
      ['Personnel fixe mensuel', etat?.categories?.personnel_fixe?.lignes || []],
      ['Enseignants fixes mensuels', etat?.categories?.enseignants_fixes?.lignes || []],
      ['Enseignants à taux horaire', etat?.categories?.enseignants_horaires?.lignes || []],
      ['Employés hybrides - synthèse non additionnée au total général', etat?.categories?.employes_hybrides?.lignes || []]
    ];

    sections.forEach(([sectionTitle, lignes]) => {
      if (!lignes.length) return;
      writeLine(sectionTitle, { size: 10, bold: true, color: [49, 145, 144], gap: 7 });
      lignes.forEach((ligne) => {
        ensureSpace(24);
        writeLine(`${ligne.nom_complet || '-'} - ${ligne.role || '-'}`, { size: 9, bold: true, gap: 5 });
        writeLine(`Brut : ${formatMontant(ligne.salaire_brut)} | Retenues : ${formatMontant(ligne.retenues_total)} | Avances : ${formatMontant(ligne.avances_total)}`, { indent: 4, gap: 4 });
        writeLine(`Net a payer : ${formatMontant(ligne.net_a_payer)} | Deja paye : ${formatMontant(ligne.deja_paye)} | Reste a doter : ${formatMontant(ligne.reste_a_payer)}`, { indent: 4, gap: 5 });
        if (ligne.alertes?.length) {
          writeLine(`Alertes : ${ligne.alertes.join(' ; ')}`, { indent: 4, color: [180, 83, 9], gap: 5 });
        }
        const datesManquantes = Array.isArray(ligne.pointages?.dates_manquantes) ? ligne.pointages.dates_manquantes : [];
        if (datesManquantes.length) {
          writeLine(`Dates manquantes : ${datesManquantes.map(formatMissingPointageDate).join(', ')}`, { indent: 4, color: [180, 83, 9], gap: 5 });
        }
        y += 2;
      });
    });

    const fileName = `etat-salaires-${normalizeFilePart(monthLabel) || 'mois'}-${Date.now()}.pdf`;
    const pdfBlob = doc.output('blob');

    if (typeof File !== 'undefined') {
      return new File([pdfBlob], fileName, { type: 'application/pdf' });
    }

    return pdfBlob;
  };

  const handleDemanderDotation = async () => {
    if (!etat || montantDotation <= 0) {
      showToast('Aucune dotation salaire à demander pour ce mois.', 'info');
      return;
    }

    if (!canRequestSalaryDotation) {
      showToast(dotationLockMessage, 'error');
      return;
    }

    setSendingDotation(true);
    showToast('Envoi de la demande de dotation salaires...', 'loading');

    try {
      const justificatifFile = buildDotationJustificatifFile();
      const motif = `Etat des salaires ${monthLabel} - dotation salaire de ${formatMontant(montantDotation)} pour ${resume.effectif_unique || resume.effectif || 0} employe(s).`;
      const response = await api.portefeuille.demanderDotation({
        montant: montantDotation,
        usage_dotation: 'principale',
        motif,
        justificatif: `Demande generee depuis l'etat des salaires. Le fichier joint contient le detail des salaires, avances, retenues et nets a payer.`,
        justificatif_fichier: justificatifFile,
        annee_scolaire_id: selectedYearId,
        objet_dotation: 'salaires',
        mois: etat?.mois_scolaire?.mois_civil || params.mois,
        annee: etat?.mois_scolaire?.annee_civile || params.annee,
        mois_scolaire_id: etat?.mois_scolaire?.id
      });

      if (!response.success) {
        throw new Error(response.message || 'Erreur lors de la demande de dotation');
      }

      setDotationSent(true);
      setShowDotationConfirm(false);
      showToast('Demande de dotation salaires envoyée à la caisse centrale.', 'success');
    } catch (error) {
      console.error('Erreur demande dotation salaires:', error);
      showToast(error.message || 'Erreur lors de la demande de dotation salaires', 'error');
    } finally {
      setSendingDotation(false);
    }
  };

  const handleValiderEtat = async () => {
    if (!canValidateEtat && !etatValidated) {
      showToast(validationLockMessage, 'error');
      return;
    }
    if (etatValidated) {
      showToast('Cet état des salaires est déjà validé pour ce mois.', 'info');
      setShowValidationConfirm(false);
      return;
    }
    setValidatingEtat(true);
    try {
      const response = await api.etatSalaires.validerEtat(params);
      if (!response.success) throw new Error(response.message || response.error || "Erreur lors de la validation de l'état des salaires");
      if (response.data) setEtat(response.data);
      setShowValidationConfirm(false);
      showToast(response.message || 'État des salaires validé pour ce mois.', 'success');
    } catch (error) {
      console.error('Erreur validation etat salaires:', error);
      showToast(error.message || "Erreur lors de la validation de l'état des salaires", 'error');
    } finally {
      setValidatingEtat(false);
    }
  };

  const hybrides = etat?.categories?.employes_hybrides;
  const hasHybrides = Boolean(hybrides?.lignes?.length);

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full space-y-6">

        <EtatSalairesHeader
          monthLabel={monthLabel}
          enLecture={loading}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          formatDateForDisplay={formatDateForDisplay}
          goToPreviousMonth={goToPreviousMonth}
          goToNextMonth={goToNextMonth}
          onBackToDashboard={onBackToDashboard}
          montantDotation={montantDotation}
          effectif={resume.effectif_unique || resume.effectif || 0}
          dejaPayes={resume.deja_payes || 0}
          sendingDotation={sendingDotation}
          dotationSent={dotationSent}
          canRequestSalaryDotation={canRequestSalaryDotation}
          canValidateEtat={canValidateEtat}
          etatValidated={etatValidated}
          validatingEtat={validatingEtat}
          onDemanderDotation={() => setShowDotationConfirm(true)}
          onValiderEtat={() => setShowValidationConfirm(true)}
        />

        {loading ? (
          <div className="px-6">
            <div className="flex min-h-[360px] items-center justify-center">
              <LoadingSpinner message="Chargement de l'état des salaires..." color="#246990" />
            </div>
          </div>
        ) : (
          <>
            {/* Contrôles du mois */}
            {showSalaryStateNotices && (
              <div className="px-6">
                <div className={`rounded-lg border-2 p-4 ${salaryNoticeTone.panel}`}>
                  <div className="flex items-start gap-3">
                    {hasSalaryBlockingNotice ? (
                      <Lock size={20} className={`${salaryNoticeTone.icon} mt-0.5 flex-shrink-0`} />
                    ) : (
                      <ShieldCheck size={20} className={`${salaryNoticeTone.icon} mt-0.5 flex-shrink-0`} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h5 className={`font-semibold ${salaryNoticeTone.title}`}>Contrôles du mois</h5>
                          <p className={`mt-1 text-sm ${salaryNoticeTone.text}`}>Demande de dotation et validation comptable pour {monthLabel}.</p>
                        </div>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm">{monthLabel}</span>
                      </div>

                      <div className={`mt-4 divide-y rounded-md border bg-white/70 ${salaryNoticeTone.list}`}>
                        {!canRequestSalaryDotation && (
                          <div className="flex items-start gap-3 px-3 py-3">
                            <Lock size={17} className="mt-0.5 flex-shrink-0 text-amber-600" />
                            <div>
                              <p className="text-sm font-semibold text-amber-950">{dotationLockTitle}</p>
                              <p className="mt-0.5 text-sm text-amber-800">{dotationLockMessage}</p>
                            </div>
                          </div>
                        )}

                        {showValidationNotice && (
                          <div className="flex items-start gap-3 px-3 py-3 last:pb-3">
                            {etatValidated || canValidateEtat ? (
                              <ShieldCheck size={17} className={`${etatValidated ? 'text-green-600' : 'text-blue-600'} mt-0.5 flex-shrink-0`} />
                            ) : (
                              <Lock size={17} className="mt-0.5 flex-shrink-0 text-amber-600" />
                            )}
                            <div>
                              <p className={`text-sm font-semibold ${etatValidated ? 'text-green-950' : canValidateEtat ? 'text-blue-950' : 'text-amber-950'}`}>{validationInfoTitle}</p>
                              <p className={`mt-0.5 text-sm ${etatValidated ? 'text-green-800' : canValidateEtat ? 'text-blue-800' : 'text-amber-800'}`}>{validationStatusMessage}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <EtatSalairesStatsCards
              resume={resume}
              monthLabel={monthLabel}
              isLoading={loading}
            />

            <RetenuesNonImputeesPanel resume={retenuesNonImputeesResume} />

            <CalendrierPaieSection
              isExpanded={expandedSections.has('calendrier')}
              onToggleSection={() => toggleSection('calendrier')}
              calendrier={calendrier}
              detail={etat?.calendrier_detail || null}
              peutOuvrir={peutOuvrirCalendrier}
              onDemanderOuverture={(etape) => ouvrirFenetreOuverture('demander', etape)}
              onRefermerOuverture={(etape) => ouvrirFenetreOuverture('refermer', etape)}
            />

            {/* Détail des lignes de paie */}
            <div className="px-6">
              <Card>
                <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => toggleSection('lignes')}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {expandedSections.has('lignes') ? (
                        <ChevronDown size={24} className="text-gray-600" />
                      ) : (
                        <ChevronRight size={24} className="text-gray-600" />
                      )}
                      <div>
                        <CardTitle className="text-xl text-purple-600 flex items-center gap-2">
                          <Users size={24} />
                          Détail des lignes de paie
                        </CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          brut, retenues, avances et net à payer par catégorie de personnel
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                {expandedSections.has('lignes') && (
                  <CardContent className="pt-6">
                    {hasHybrides && (
                      <SalaryTableSection
                        title="Employés hybrides"
                        description="Synthèse consolidant les composantes fixes et horaires. Cette section ne s'ajoute pas au total général de dotation."
                        data={hybrides}
                        tone="hybrides"
                      />
                    )}

                    <SalaryTableSection
                      title="Personnel fixe mensuel"
                      description="Administratifs et personnel non enseignant avec salaire mensuel."
                      data={etat?.categories?.personnel_fixe}
                      tone="personnel"
                    />

                    <SalaryTableSection
                      title="Enseignants fixes mensuels"
                      description="Enseignants payés au mois, avec retenues et avances."
                      data={etat?.categories?.enseignants_fixes}
                      tone="enseignantsFixes"
                    />

                    <SalaryTableSection
                      title="Enseignants à taux horaire"
                      description="Montant calculé depuis les pointages horaires envoyés en comptabilité."
                      data={etat?.categories?.enseignants_horaires}
                      tone="enseignantsHoraires"
                    />

                    {!!resume.alertes && (
                      <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <h5 className="font-semibold text-amber-900">
                              {resume.alertes} ligne(s) en alerte de lecture
                            </h5>
                            <p className="text-sm text-amber-800 mt-1">
                              Vérifiez les pointages ou les données de paie avant de demander la dotation.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            </div>
          </>
        )}
      </div>

      <OuvertureFenetreDialog
        key={cleOuverture}
        open={Boolean(ouvertureEnCours)}
        mode={ouvertureEnCours?.mode}
        etape={ouvertureEnCours?.etape}
        ouverture={ouvertureEnCours?.etape?.ouverture}
        mois={etat?.mois_scolaire}
        onClose={() => setOuvertureEnCours(null)}
        onTermine={(resultat) => {
          setOuvertureEnCours(null);
          showToast(resultat?.message || 'Demande envoyée', 'success');
          loadEtat();
        }}
      />

      {/* ============================================================== */}
      {/* Valider l'état — format KLASYS (DESIGN-SYSTEM.md)              */}
      {/* ============================================================== */}
      <Dialog open={showValidationConfirm} onOpenChange={(ouvert) => { if (!ouvert && !validatingEtat) setShowValidationConfirm(false); }}>
        <DialogContent className={CONTENU_DIALOG}>
          <DialogHeader className={ENTETE_DIALOG}>
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <ShieldCheck size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Valider l'état des salaires</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                La comptabilité confirme avoir contrôlé l'état, et que la dotation centrale couvre le net restant à payer.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <CarteRecap libelle="Mois" valeur={monthLabel} />
              <CarteRecap libelle="Montant à débloquer" valeur={formatMontant(montantDotation)} accent />
              <CarteRecap libelle="Effectif concerné" valeur={`${resume.effectif_unique || resume.effectif || 0} employé(s)`} />
            </div>
            <p className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-4 py-3 text-sm text-[#246990]">
              Les modules de paiement seront débloqués pour ce mois. La validation est enregistrée sur le mois lu : si
              l'état change ensuite et que le montant à doter augmente, une nouvelle validation sera requise.
            </p>
          </div>

          <DialogFooter className={PIED_DIALOG}>
            <button type="button" onClick={() => setShowValidationConfirm(false)} disabled={validatingEtat} className={BOUTON_SECONDAIRE}>
              Annuler
            </button>
            <button
              type="button"
              onClick={handleValiderEtat}
              disabled={validatingEtat || !canValidateEtat || etatValidated}
              className={BOUTON_PRINCIPAL}
            >
              {validatingEtat ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {validatingEtat ? 'Validation...' : "Valider l'état"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================== */}
      {/* Demander la dotation — format KLASYS                           */}
      {/* ============================================================== */}
      <Dialog open={showDotationConfirm} onOpenChange={(ouvert) => { if (!ouvert && !sendingDotation) setShowDotationConfirm(false); }}>
        <DialogContent className={CONTENU_DIALOG}>
          <DialogHeader className={ENTETE_DIALOG}>
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Send size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Demander la dotation salaires</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                La demande part à la caisse centrale, avec l'état des salaires en justificatif.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <CarteRecap libelle="Mois" valeur={monthLabel} />
              <CarteRecap libelle="Montant demandé" valeur={formatMontant(montantDotation)} accent />
              <CarteRecap libelle="Effectif concerné" valeur={`${resume.effectif_unique || resume.effectif || 0} employé(s)`} />
            </div>
            <p className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-4 py-3 text-sm text-[#246990]">
              Le fichier joint contient le brut, les retenues, les avances, le net à payer et le reste à doter par ligne de paie.
            </p>
          </div>

          <DialogFooter className={PIED_DIALOG}>
            <button type="button" onClick={() => setShowDotationConfirm(false)} disabled={sendingDotation} className={BOUTON_SECONDAIRE}>
              Annuler
            </button>
            <button
              type="button"
              onClick={handleDemanderDotation}
              disabled={sendingDotation || montantDotation <= 0 || dotationSent || !canRequestSalaryDotation}
              className={BOUTON_PRINCIPAL}
            >
              {sendingDotation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sendingDotation ? 'Envoi...' : 'Envoyer la demande'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EtatSalaires;
